/**
 * VISALL 智能抽样策略
 * 支持多种抽样方法，优化大数据集验证性能
 */

import type { TabularData, SamplingConfig } from './types';

export class SamplingStrategy {
  private config: SamplingConfig;

  constructor(config: Partial<SamplingConfig> = {}) {
    this.config = {
      threshold: 1000, // 默认阈值1000条
      strategy: 'intelligent', // 默认智能抽样
      sampleSize: 500, // 默认抽样数量500条
      ...config
    };
  }

  /**
   * 判断是否需要抽样
   */
  shouldSample(data: TabularData): boolean {
    return data.length >= this.config.threshold;
  }

  /**
   * 执行抽样
   */
  sample(data: TabularData): TabularData {
    if (!this.shouldSample(data)) {
      return data;
    }

    const startTime = performance.now();
    let result: TabularData;

    switch (this.config.strategy) {
      case 'fixed':
        result = this.fixedSample(data);
        break;
      case 'random':
        result = this.randomSample(data);
        break;
      case 'intelligent':
      default:
        result = this.intelligentSample(data);
        break;
    }

    const duration = performance.now() - startTime;
    console.log(
      `SamplingStrategy: ${this.config.strategy} sampling completed in ${duration.toFixed(2)}ms, ` +
        `${data.length} -> ${result.length} rows`
    );

    return result;
  }

  /**
   * 固定抽样：取前N条数据
   */
  private fixedSample(data: TabularData): TabularData {
    return data.slice(0, this.config.sampleSize);
  }

  /**
   * 随机抽样：随机选择N条数据
   */
  private randomSample(data: TabularData): TabularData {
    const sampleSize = Math.min(this.config.sampleSize, data.length);
    const result: TabularData = [];
    const indices = new Set<number>();

    // 生成不重复的随机索引
    while (indices.size < sampleSize) {
      const randomIndex = Math.floor(Math.random() * data.length);
      indices.add(randomIndex);
    }

    // 按索引顺序采样，保持数据的相对顺序
    const sortedIndices = Array.from(indices).sort((a, b) => a - b);
    sortedIndices.forEach(index => {
      result.push(data[index]);
    });

    return result;
  }

  /**
   * 🧠 智能抽样：基于数据分布的分层抽样
   * 确保抽样数据能够代表原始数据的特征
   */
  private intelligentSample(data: TabularData): TabularData {
    const sampleSize = Math.min(this.config.sampleSize, data.length);

    // 1. 分析数据特征
    const dataAnalysis = this.analyzeDataDistribution(data);

    // 2. 基于分析结果进行分层抽样
    return this.stratifiedSample(data, sampleSize, dataAnalysis);
  }

  /**
   * 分析数据分布特征
   */
  private analyzeDataDistribution(data: TabularData): DataDistributionAnalysis {
    const analysis: DataDistributionAnalysis = {
      categoricalFields: new Map(),
      numericFields: new Map(),
      dateFields: new Map(),
      totalRows: data.length
    };

    if (data.length === 0) {
      return analysis;
    }

    // 分析字段类型和分布
    const sampleRow = data[0];
    Object.keys(sampleRow).forEach(fieldName => {
      const fieldValues = data.map(row => row[fieldName]).filter(val => val != null);

      if (fieldValues.length === 0) {
        return;
      }

      // 判断字段类型
      const firstValue = fieldValues[0];

      if (typeof firstValue === 'string') {
        // 检查是否为日期字段
        if (this.isDateField(fieldValues)) {
          const dates = fieldValues
            .map(val => new Date(val as string))
            .filter(date => !isNaN(date.getTime()));
          analysis.dateFields.set(fieldName, {
            count: dates.length,
            min: new Date(Math.min(...dates.map(d => d.getTime()))),
            max: new Date(Math.max(...dates.map(d => d.getTime()))),
            samples: dates.slice(0, 10)
          });
        } else {
          // 分类字段
          const uniqueValues = new Set(fieldValues);
          analysis.categoricalFields.set(fieldName, {
            uniqueCount: uniqueValues.size,
            totalCount: fieldValues.length,
            categories: Array.from(uniqueValues).slice(0, 20), // 最多保存20个类别
            distribution: this.calculateCategoryDistribution(fieldValues)
          });
        }
      } else if (typeof firstValue === 'number') {
        // 数值字段
        const numbers = fieldValues.filter(val => typeof val === 'number') as number[];
        analysis.numericFields.set(fieldName, {
          count: numbers.length,
          min: Math.min(...numbers),
          max: Math.max(...numbers),
          mean: numbers.reduce((sum, val) => sum + val, 0) / numbers.length,
          samples: numbers.slice(0, 10)
        });
      }
    });

    return analysis;
  }

  /**
   * 分层抽样实现
   */
  private stratifiedSample(
    data: TabularData,
    sampleSize: number,
    analysis: DataDistributionAnalysis
  ): TabularData {
    const result: TabularData = [];

    // 🎯 策略1: 如果有分类字段，按分类分层抽样
    const primaryCategoricalField = this.findPrimaryCategoricalField(analysis);

    if (primaryCategoricalField) {
      return this.categoricalStratifiedSample(data, sampleSize, primaryCategoricalField);
    }

    // 🎯 策略2: 如果有日期字段，按时间分层抽样
    const primaryDateField = this.findPrimaryDateField(analysis);

    if (primaryDateField) {
      return this.temporalStratifiedSample(data, sampleSize, primaryDateField);
    }

    // 🎯 策略3: 回退到增强的均匀抽样
    return this.enhancedUniformSample(data, sampleSize);
  }

  /**
   * 按分类字段分层抽样
   */
  private categoricalStratifiedSample(
    data: TabularData,
    sampleSize: number,
    fieldName: string
  ): TabularData {
    // 按分类分组
    const groups = new Map<string, TabularData>();
    data.forEach(row => {
      const category = String(row[fieldName] || 'undefined');
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(row);
    });

    const result: TabularData = [];
    const categories = Array.from(groups.keys());

    // 计算每个分类的抽样数量
    categories.forEach(category => {
      const categoryData = groups.get(category)!;
      const categoryRatio = categoryData.length / data.length;
      const categorySampleSize = Math.max(1, Math.floor(sampleSize * categoryRatio));

      // 从每个分类中均匀抽样
      const categoryStep = Math.max(1, Math.floor(categoryData.length / categorySampleSize));
      for (let i = 0; i < categoryData.length && result.length < sampleSize; i += categoryStep) {
        result.push(categoryData[i]);
      }
    });

    return result.slice(0, sampleSize);
  }

  /**
   * 按时间字段分层抽样
   */
  private temporalStratifiedSample(
    data: TabularData,
    sampleSize: number,
    fieldName: string
  ): TabularData {
    // 按时间排序
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a[fieldName] as string).getTime();
      const dateB = new Date(b[fieldName] as string).getTime();
      return dateA - dateB;
    });

    // 时间均匀分布抽样
    const step = Math.max(1, Math.floor(sortedData.length / sampleSize));
    const result: TabularData = [];

    for (let i = 0; i < sortedData.length && result.length < sampleSize; i += step) {
      result.push(sortedData[i]);
    }

    // 确保包含首尾数据
    if (result.length > 0 && result[0] !== sortedData[0]) {
      result[0] = sortedData[0];
    }
    if (result.length > 1 && result[result.length - 1] !== sortedData[sortedData.length - 1]) {
      result[result.length - 1] = sortedData[sortedData.length - 1];
    }

    return result;
  }

  /**
   * 增强的均匀抽样
   */
  private enhancedUniformSample(data: TabularData, sampleSize: number): TabularData {
    const result: TabularData = [];
    const step = Math.max(1, Math.floor(data.length / sampleSize));

    // 均匀间隔抽样
    for (let i = 0; i < data.length && result.length < sampleSize; i += step) {
      result.push(data[i]);
    }

    // 确保包含最后一条数据（捕获边界情况）
    if (result.length < sampleSize && data.length > 0) {
      const lastItem = data[data.length - 1];
      if (result[result.length - 1] !== lastItem) {
        result.push(lastItem);
      }
    }

    return result;
  }

  /**
   * 工具方法：判断是否为日期字段
   */
  private isDateField(values: unknown[]): boolean {
    if (values.length === 0) {
      return false;
    }

    // 取前10个值进行检测
    const sampleValues = values.slice(0, 10);
    const validDates = sampleValues.filter(val => {
      if (typeof val !== 'string') {
        return false;
      }
      const date = new Date(val);
      return !isNaN(date.getTime());
    });

    // 如果70%以上的值都是有效日期，认为是日期字段
    return validDates.length / sampleValues.length >= 0.7;
  }

  /**
   * 计算分类分布
   */
  private calculateCategoryDistribution(values: unknown[]): Map<string, number> {
    const distribution = new Map<string, number>();
    values.forEach(val => {
      const key = String(val);
      distribution.set(key, (distribution.get(key) || 0) + 1);
    });
    return distribution;
  }

  /**
   * 找到主要的分类字段
   */
  private findPrimaryCategoricalField(analysis: DataDistributionAnalysis): string | null {
    let bestField: string | null = null;
    let bestScore = 0;

    analysis.categoricalFields.forEach((info, fieldName) => {
      // 评分：适中的唯一值数量（2-50）得分最高
      const uniqueRatio = info.uniqueCount / info.totalCount;
      let score = 0;

      if (info.uniqueCount >= 2 && info.uniqueCount <= 50 && uniqueRatio < 0.8) {
        score = Math.min(info.uniqueCount, 20) * (1 - uniqueRatio);
      }

      if (score > bestScore) {
        bestScore = score;
        bestField = fieldName;
      }
    });

    return bestField;
  }

  /**
   * 找到主要的日期字段
   */
  private findPrimaryDateField(analysis: DataDistributionAnalysis): string | null {
    // 返回第一个日期字段
    const dateFields = Array.from(analysis.dateFields.keys());
    return dateFields.length > 0 ? dateFields[0] : null;
  }

  /**
   * 获取抽样统计信息
   */
  getSamplingStats() {
    return {
      config: this.config,
      lastSampleRatio: null // 可以在sample方法中更新
    };
  }
}

// 类型定义
interface DataDistributionAnalysis {
  categoricalFields: Map<string, CategoricalFieldInfo>;
  numericFields: Map<string, NumericFieldInfo>;
  dateFields: Map<string, DateFieldInfo>;
  totalRows: number;
}

interface CategoricalFieldInfo {
  uniqueCount: number;
  totalCount: number;
  categories: unknown[];
  distribution: Map<string, number>;
}

interface NumericFieldInfo {
  count: number;
  min: number;
  max: number;
  mean: number;
  samples: number[];
}

interface DateFieldInfo {
  count: number;
  min: Date;
  max: Date;
  samples: Date[];
}
